import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FeatureMarker } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { isOneSizeProduct } from '@as-react/common-utils';
import { useIsClient } from '@yonderland/andes-react';
import { useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop, Badge, TextAlpha, Banner, Box, Stack, } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ProductComparisonAPI from '../../api/product-comparison/ProductComparisonAPI';
import ProductDetailAPI from '../../api/ProductDetailAPI';
import ProductsAPI from '../../api/ProductsAPI';
import { CompareCarousel } from '../../components/product-detail/compare-carousel/CompareCarousel';
import { CompareEmptyPage } from '../../components/product-detail/compare-empty-page/CompareEmptyPage';
import { CompareFeatures } from '../../components/product-detail/compare-features/CompareFeatures';
import { CompareProductsBar } from '../../components/product-detail/compare-products-bar/CompareProductsBar';
import { CompareStickyBar } from '../../components/product-detail/compare-sticky-bar/CompareStickyBar';
import { CompareStickyBarMobile, MAIN_COMPARE_CAROUSEL_ID, } from '../../components/product-detail/compare-sticky-bar/CompareStickyBarMobile';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { PRODUCT_COMPARE_LIST } from '../../constants/StorageKeysConstants';
import { ProductAddToBasketLocations } from '../../enums/ActionEnums';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { basketActions } from '../../redux/basket/actions/BasketActions.creators';
import compareActions from '../../redux/compare/actions/CompareActions.creators';
import { makeGetCompareState } from '../../redux/compare/selectors/CompareSelectors';
import { productComparisonActions } from '../../redux/compare-page/actions/ProductComparison.creators';
import { makeGetProductComparisonState } from '../../redux/compare-page/selectors/ProductComparisonSelectors';
import { getAttributesForProducts, getComparisonProducts } from '../../util/CompareUtil';
import LocalStorageUtil from '../../util/LocalStorageUtil';
import { isClickAndCollectOnly, isInStoreOnly, isProductOutOfStock, isProductWithColorOutOfStock, } from '../../util/ProductUtil';
import { generateTestId } from '../../util/TestUtil';
import BasketPopupContainer from '../BasketPopupContainer';
import { MiniPDPContainer } from '../MiniPDPContainer';
const TEST_ID = 'pdp-comparison';
const ProductComparisonContainer = ({ deliveryPromiseExplanations }) => {
    var _a;
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const isMobile = useMediaQueryMatchesOnMobile();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const { defaultRequestParams, servicesEndpoint, oneSizeProductCode, locale } = useAemConfig();
    const [loadingProducts, setLoadingProducts] = useState(true);
    const isClient = useIsClient();
    const compareState = useSelector(makeGetCompareState());
    const comparisonProducts = useSelector(makeGetProductComparisonState());
    const dispatch = useDispatch();
    const fetchComparisonProducts = (products) => dispatch(productComparisonActions.fetchProducts(products));
    const compareItemSelectSize = (payload) => dispatch(productComparisonActions.createSelectSizeAction(payload));
    const getDeliveryPromise = (payload) => dispatch(productComparisonActions.getDeliveryPromise(payload));
    const [attributes, setAttributes] = useState([]);
    const [fetchedComparisonProducts, setFetchedComparisonProducts] = useState(false);
    const [miniPDPData, setMiniPDPData] = useState(null);
    const [compareSwiperInstance, setCompareSwiperInstance] = useState(null);
    const [compareBarSwiperInstance, setCompareBarSwiperInstance] = useState(null);
    const [compareStickyBarSwiperInstance, setCompareStickyBarSwiperInstance] = useState(null);
    const [compareStickyBarMobileSwiperInstance, setCompareStickyBarMobileSwiperInstance] = useState(null);
    const [compareFeaturesSwiperInstances, setCompareFeaturesSwiperInstances] = useState([]);
    const [amountProductOrColorOutOfStock, setAmountProductOrColorOutOfStock] = useState(0);
    const bindSwiperInstances = (mainSwiper, additionalSwipers) => {
        if (mainSwiper === null || mainSwiper === void 0 ? void 0 : mainSwiper.controller) {
            mainSwiper.controller.control = additionalSwipers;
            additionalSwipers.forEach(swiper => {
                if (swiper === null || swiper === void 0 ? void 0 : swiper.controller) {
                    swiper.controller.control = mainSwiper;
                }
            });
            mainSwiper.update();
        }
    };
    const updateSwiperBinding = (comparisonAttributes) => {
        const swiperFeatureInstances = compareFeaturesSwiperInstances
            .filter(swiper => !!comparisonAttributes.find(feature => swiper.attribute === feature.attribute))
            .map(featureSwiper => featureSwiper.swiper);
        const swiperInstances = [
            compareBarSwiperInstance,
            isMobile ? compareStickyBarMobileSwiperInstance : compareStickyBarSwiperInstance,
            ...swiperFeatureInstances,
        ].filter(Boolean);
        bindSwiperInstances(compareSwiperInstance, swiperInstances);
    };
    useEffect(() => {
        dispatch(productComparisonActions.getInitialComparisonProducts());
    }, []);
    useEffect(() => {
        const compareData = LocalStorageUtil.getItemWithExpiry(PRODUCT_COMPARE_LIST);
        dispatch(compareActions.createCompareProductDisplayedToggle(Boolean(compareData && compareData.length > 0)));
    }, []);
    useEffect(() => {
        const swiperInstances = [
            compareBarSwiperInstance,
            isMobile ? compareStickyBarMobileSwiperInstance : compareStickyBarSwiperInstance,
            ...compareFeaturesSwiperInstances.map(swiper => swiper.swiper),
        ].filter(Boolean);
        bindSwiperInstances(compareSwiperInstance, swiperInstances);
    }, [
        isMobile,
        compareSwiperInstance,
        compareBarSwiperInstance,
        compareStickyBarSwiperInstance,
        compareStickyBarMobileSwiperInstance,
        compareFeaturesSwiperInstances,
    ]);
    useEffect(() => {
        if (!isClient) {
            return;
        }
        if (compareState.items.length === 0) {
            fetchComparisonProducts([]);
            setAttributes([]);
            setLoadingProducts(false);
        }
        if (compareState.items.length > 0) {
            const productsActualized = compareState.items.map((item) => ({
                productCode: item.productCode,
                colorId: item.color,
            }));
            const getFullDescribedProducts = () => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                const productsWithInfo = yield ProductsAPI.getAllProductInformation(productsActualized, servicesEndpoint, defaultRequestParams);
                const productsWithAttributes = yield ProductComparisonAPI.getCompareFeatures(productsWithInfo, servicesEndpoint, defaultRequestParams, locale);
                const productsDetailPrice = yield ProductDetailAPI.getAllProductDetailPriceByProductId(productsWithInfo.map((product) => product.productId), servicesEndpoint, defaultRequestParams);
                const productsOutOfStock = productsWithInfo.map(product => isProductOutOfStock(product));
                const attributesForComparison = getAttributesForProducts(productsWithAttributes, productsOutOfStock).filter(attribute => attribute.valuesByProducts.some(value => !!value && value !== FeatureMarker.ABSENCE && value !== FeatureMarker.OUT_OF_STOCK));
                const countProductOrColorOutOfStock = productsWithInfo.filter(product => isProductOutOfStock(product) || isProductWithColorOutOfStock(product)).length;
                setAmountProductOrColorOutOfStock(countProductOrColorOutOfStock);
                const products = getComparisonProducts(productsWithInfo, productsDetailPrice);
                if (!fetchedComparisonProducts) {
                    fetchComparisonProducts(products);
                }
                const compareFeaturesAnalyticsData = (_a = productsWithAttributes === null || productsWithAttributes === void 0 ? void 0 : productsWithAttributes.items) === null || _a === void 0 ? void 0 : _a.map(item => ({
                    id: item.productId,
                    featuresAmount: Object.keys(item.features).length,
                }));
                if (compareFeaturesAnalyticsData === null || compareFeaturesAnalyticsData === void 0 ? void 0 : compareFeaturesAnalyticsData.length) {
                    dispatch(analyticsActions.compareProductsFeatures(compareFeaturesAnalyticsData));
                }
                setAttributes(attributesForComparison);
                updateSwiperBinding(attributesForComparison);
                setFetchedComparisonProducts(true);
                setLoadingProducts(false);
            });
            getFullDescribedProducts();
        }
    }, [compareState]);
    const onCloseClick = (productCode, colourId) => {
        const productIndex = comparisonProducts.findIndex((product) => product.productCode === productCode && product.colourId === colourId);
        dispatch(productComparisonActions.removeProduct(productIndex));
        dispatch(compareActions.createUpdateInProductComparePageAction(compareState.items[productIndex]));
    };
    const _addToBasket = (payload) => {
        const selectedColor = payload.product.colours.find((color) => color.colourId === payload.colorId);
        const oneSizeProduct = isOneSizeProduct(selectedColor.sizes, oneSizeProductCode);
        if ((isDesktop || oneSizeProduct) && !isInStoreOnly(payload.product) && !isClickAndCollectOnly(payload.product)) {
            dispatch(basketActions.createAddExtensiveTileProductAction(payload, ProductAddToBasketLocations.PRODUCT_COMPARE));
        }
        else {
            setMiniPDPData(payload);
        }
    };
    const onMiniPDPClose = () => setMiniPDPData(null);
    if (!isClient || loadingProducts)
        return null;
    return (_jsxs(Box, { paddingBlockStart: { base: '4', lg: '0' }, testId: TEST_ID, children: [_jsxs(Stack, { align: "center", gap: { base: '2', md: '3' }, children: [_jsx(TextAlpha, { as: "h2", variant: "subheadingXL", children: t('productCompare.pdp.checkbox') }), _jsx(Badge, { color: "branded", testId: generateTestId({ testId: TEST_ID, suffix: 'length' }), children: comparisonProducts.length })] }), (compareState === null || compareState === void 0 ? void 0 : compareState.items.length) === 0 ? (_jsx(CompareEmptyPage, {})) : (_jsxs(Box, { paddingBlockStart: "6", children: [isMobile && amountProductOrColorOutOfStock > 0 && (_jsx(Box, { paddingBlockEnd: "4", children: _jsx(Banner, { status: "informative", text: t(amountProductOrColorOutOfStock > 1
                                ? 'product.some.out.of.stock.feedback.several'
                                : 'product.some.out.of.stock.feedback') }) })), _jsx("div", { id: MAIN_COMPARE_CAROUSEL_ID, children: _jsx(CompareCarousel, { setSwiperInstance: setCompareSwiperInstance, onCloseClick: onCloseClick, comparisonProducts: comparisonProducts, deliveryPromiseExplanations: deliveryPromiseExplanations, addToBasket: _addToBasket, getDeliveryPromise: getDeliveryPromise, compareItemSelectSize: compareItemSelectSize }) }), attributes.length > 0 && (_jsx(Box, { paddingBlockStart: "4", children: _jsx(CompareFeatures, { attributes: attributes, compareFeaturesSwiperInstances: compareFeaturesSwiperInstances, setSwiperInstances: setCompareFeaturesSwiperInstances }) })), _jsx(Box, { paddingBlockStart: { base: '4', md: '10', lg: '12' }, paddingBlockEnd: { base: '6', md: '10' }, children: _jsx(CompareProductsBar, { setSwiperInstance: setCompareBarSwiperInstance, comparisonProducts: comparisonProducts, addToBasket: _addToBasket, deliveryPromiseExplanations: deliveryPromiseExplanations, getDeliveryPromise: getDeliveryPromise, compareItemSelectSize: compareItemSelectSize }) }), isMobile ? (_jsx(CompareStickyBarMobile, { setSwiperInstance: setCompareStickyBarMobileSwiperInstance, onCloseClick: onCloseClick, comparisonProducts: comparisonProducts, addToBasket: _addToBasket })) : (_jsx(CompareStickyBar, { setSwiperInstance: setCompareStickyBarSwiperInstance, comparisonProducts: comparisonProducts })), miniPDPData && (_jsx(MiniPDPContainer, { productCode: (_a = miniPDPData === null || miniPDPData === void 0 ? void 0 : miniPDPData.product) === null || _a === void 0 ? void 0 : _a.productCode, colorId: miniPDPData === null || miniPDPData === void 0 ? void 0 : miniPDPData.colorId, onClose: onMiniPDPClose, deliveryPromiseExplanations: deliveryPromiseExplanations }))] })), _jsx(BasketPopupContainer, {})] }));
};
export default ProductComparisonContainer;
